import LanguagesPage from './pages/admin/LanguagesPage';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { Component, ReactNode } from 'react';
import { UserProvider, useCurrentUser } from './context/UserContext';
import Layout from './components/layout/Layout';

// ─── Error Boundary ──────────────────────────────────────────────────────────

class PageErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; message: string }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error?.message ?? 'Unknown error' };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-3 p-8">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <span className="text-red-600 text-xl font-bold">!</span>
          </div>
          <p className="text-gray-700 font-medium text-center">Something went wrong loading this page.</p>
          <p className="text-gray-400 text-sm text-center font-mono">{this.state.message}</p>
          <button
            onClick={() => this.setState({ hasError: false, message: '' })}
            className="px-4 py-2 text-sm rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import ProjectsPage from './pages/ProjectsPage';
import SessionsPage from './pages/SessionsPage';
import SessionDetailPage from './pages/SessionDetailPage';
import MapPage from './pages/MapPage';
import AnnotationTaskListPage from './pages/annotation/AnnotationTaskListPage';
import MyStatsPage from './pages/transcriber/MyStatsPage';
import AnnotationWorkspacePage from './pages/annotation/AnnotationWorkspacePage';
import AssignmentsPage from './pages/admin/AssignmentsPage';
import AnnotationAnalyticsPage from './pages/admin/AnnotationAnalyticsPage';
import TimeboundAnalyticsPage from './pages/admin/TimeboundAnalyticsPage';
import SupervisorAnalyticsPage from './pages/admin/SupervisorAnalyticsPage';
import SurveyAnalyticsPage from './pages/admin/SurveyAnalyticsPage';
import LoginPanel from './components/auth/LoginPanel';
import DataExportPage from './pages/admin/DataExportPage';
import PendingApprovalsPage from './pages/supervisor/PendingApprovalsPage';
import MyTeamAssignmentsPage from './pages/supervisor/MyTeamAssignmentsPage';

// ─── Route guards ──────────────────────────────────────────────────────────────

function AdminOnlyRoute({ children }: { children: React.ReactNode }) {
  const { role } = useCurrentUser();
  if (role !== 'admin') return <Navigate to="/annotation/tasks" replace />;
  return <>{children}</>;
}

// Allows admin OR sub-admin only.
function SubAdminOrAdminRoute({ children }: { children: React.ReactNode }) {
  const { role } = useCurrentUser();
  if (role !== 'admin' && role !== 'sub-admin') return <Navigate to="/annotation/tasks" replace />;
  return <>{children}</>;
}

// Allows admin OR supervisor (NOT sub-admin — they're a peer tier with different views).
function SupervisorOnlyRoute({ children }: { children: React.ReactNode }) {
  const { role } = useCurrentUser();
  if (role !== 'admin' && role !== 'supervisor') return <Navigate to="/annotation/tasks" replace />;
  return <>{children}</>;
}

function SupervisorRoute({ children }: { children: React.ReactNode }) {
  // Legacy guard: blocks enumerator + transcriber, allows everyone else.
  // Kept for backward compatibility with the existing dashboard / sessions
  // / projects pages that supervisors AND admins both see.
  const { role } = useCurrentUser();
  if (role === 'enumerator' || role === 'transcriber') {
    return <Navigate to="/annotation/tasks" replace />;
  }
  return <>{children}</>;
}

// ─── Auth guard ────────────────────────────────────────────────────────────────

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { authStatus } = useAuthenticator();
  const { isLoading } = useCurrentUser();

  if (authStatus === 'configuring' || (authStatus === 'authenticated' && isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (authStatus !== 'authenticated') {
    return <Navigate to="/login" replace />;
  }

  // All authenticated users are allowed through — per-route guards handle role checks
  return <>{children}</>;
}

// ─── Login redirect ────────────────────────────────────────────────────────────

function LoginRedirect({ children }: { children: React.ReactNode }) {
  const { authStatus } = useAuthenticator();
  const { role } = useCurrentUser();

  if (authStatus === 'authenticated') {
    // Enumerators go straight to annotation tasks
    if (role === 'enumerator') {
      return <Navigate to="/annotation/tasks" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// ─── Root redirect (handles / for all roles) ──────────────────────────────────

function RootRedirect() {
  const { role } = useCurrentUser();
  if (role === 'enumerator') return <Navigate to="/annotation/tasks" replace />;
  return <Navigate to="/dashboard" replace />;
}

// ─── Workspace page uses full-height layout (no padding) ──────────────────────
// The AnnotationWorkspacePage manages its own layout internally.

function WorkspaceLayout({ children }: { children: React.ReactNode }) {
  return <div className="h-full overflow-hidden">{children}</div>;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <Authenticator.Provider>
      <UserProvider>
        <Routes>
          <Route
            path="/login"
            element={
              <LoginRedirect>
                <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-900 via-brand-700 to-accent-600 p-4">
                  <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                      <img
                        src="/dataone-logo.png"
                        alt="DataOne"
                        className="w-28 h-28 mx-auto mb-4 rounded-3xl bg-white shadow-xl object-contain p-1"
                      />
                      <p className="text-brand-200">Admin Dashboard</p>
                    </div>
                    <div className="bg-white rounded-2xl shadow-2xl p-8">
                      <LoginPanel />
                    </div>
                  </div>
                </div>
              </LoginRedirect>
            }
          />
          <Route
            path="/*"
            element={
              <AuthGuard>
                <Layout>
                  <PageErrorBoundary>
                  <Routes>
                    {/* Root redirect based on role */}
                    <Route path="/" element={<RootRedirect />} />

                    {/* Admin + Supervisor routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <SupervisorRoute>
                          <DashboardPage />
                        </SupervisorRoute>
                      }
                    />
                    <Route
                      path="/users"
                      element={
                        <SupervisorRoute>
                          <UsersPage />
                        </SupervisorRoute>
                      }
                    />
                    <Route
                      path="/projects"
                      element={
                        <SupervisorRoute>
                          <ProjectsPage />
                        </SupervisorRoute>
                      }
                    />
                    <Route
                      path="/sessions"
                      element={
                        <SupervisorRoute>
                          <SessionsPage />
                        </SupervisorRoute>
                      }
                    />
                    <Route
                      path="/sessions/:sessionId"
                      element={
                        <SupervisorRoute>
                          <SessionDetailPage />
                        </SupervisorRoute>
                      }
                    />
                    <Route
                      path="/map"
                      element={
                        <SupervisorRoute>
                          <MapPage />
                        </SupervisorRoute>
                      }
                    />

                    <Route path="/admin/languages" element={<AdminOnlyRoute><LanguagesPage /></AdminOnlyRoute>} />
                    {/* Admin-only routes */}
                    <Route
                      path="/admin/assignments"
                      element={
                        <AdminOnlyRoute>
                          <AssignmentsPage />
                        </AdminOnlyRoute>
                      }
                    />
                    <Route
                      path="/admin/analytics"
                      element={
                        <SupervisorRoute>
                          <AnnotationAnalyticsPage />
                        </SupervisorRoute>
                      }
                    />
                    <Route
                      path="/admin/timebound-analytics"
                      element={
                        <SupervisorRoute>
                          <TimeboundAnalyticsPage />
                        </SupervisorRoute>
                      }
                    />
                    <Route
                      path="/admin/supervisor-analytics"
                      element={
                        <SubAdminOrAdminRoute>
                          <SupervisorAnalyticsPage />
                        </SubAdminOrAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/survey-analytics"
                      element={
                        <SupervisorRoute>
                          <SurveyAnalyticsPage />
                        </SupervisorRoute>
                      }
                    />
                    <Route
                      path="/admin/data-export"
                      element={
                        <AdminOnlyRoute>
                          <DataExportPage />
                        </AdminOnlyRoute>
                      }
                    />

                    {/* Supervisor self-service routes */}
                    <Route
                      path="/supervisor/pending-approvals"
                      element={
                        <SupervisorOnlyRoute>
                          <PendingApprovalsPage />
                        </SupervisorOnlyRoute>
                      }
                    />
                    <Route
                      path="/supervisor/my-team"
                      element={
                        <SupervisorOnlyRoute>
                          <MyTeamAssignmentsPage />
                        </SupervisorOnlyRoute>
                      }
                    />

                    {/* Annotation routes — all roles */}
                    <Route
                      path="/annotation/tasks"
                      element={<AnnotationTaskListPage />}
                    />
                    <Route
                      path="/annotation/session/:taskId"
                      element={
                        <WorkspaceLayout>
                          <AnnotationWorkspacePage />
                        </WorkspaceLayout>
                      }
                    />

                    {/* Self-stats — transcribers + enumerators see their
                        own volume here. Higher roles use Timebound
                        Analytics for platform-wide views. */}
                    <Route path="/my/stats" element={<MyStatsPage />} />
                  </Routes>
                  </PageErrorBoundary>
                </Layout>
              </AuthGuard>
            }
          />
        </Routes>
      </UserProvider>
    </Authenticator.Provider>
  );
}
